import { ChangeEvent, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { useScanPassport } from "../../../apollo/mutations/scanPassport";
import { PassportOcrResult } from "../types/passportOcr";

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

type PassportScannerProps = {
  onExtracted: (result: PassportOcrResult) => void;
};

// Reads a File and returns its raw base64 payload (without the data: prefix).
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const confidenceColor = (
  confidence: number,
): "success" | "warning" | "error" =>
  confidence >= 80 ? "success" : confidence >= 50 ? "warning" : "error";

export const PassportScanner = ({ onExtracted }: PassportScannerProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [confidence, setConfidence] = useState<number | null>(null);
  const [filled, setFilled] = useState(false);

  const [scanPassport, { loading }] = useScanPassport();

  const reset = () => {
    setError("");
    setConfidence(null);
    setFilled(false);
  };

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    reset();
    const selected = event.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError("Only JPEG, PNG, or WebP images are supported.");
      return;
    }
    if (selected.size > MAX_FILE_BYTES) {
      setError("Image is too large — please use a file under 5 MB.");
      return;
    }
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const onScan = async () => {
    if (!file) return;
    reset();
    try {
      const imageBase64 = await fileToBase64(file);
      const { data } = await scanPassport({
        variables: { imageBase64, mimeType: file.type },
      });
      const result = data?.scanPassport;
      if (!result || result.error || result.confidence === 0) {
        setError(result?.error ?? "Could not read passport. Try a clearer image.");
        return;
      }
      setConfidence(result.confidence);
      setFilled(true);
      onExtracted(result);
    } catch (e) {
      setError("Could not read passport. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        border: "2px dashed",
        borderColor: "primary.main",
        borderRadius: 2,
        p: 2,
        textAlign: "center",
      }}
    >
      <input
        accept="image/jpeg,image/png,image/webp"
        onChange={onSelect}
        style={{ display: "none" }}
        id="passport-scanner-file"
        type="file"
      />

      {preview ? (
        <Box
          component="img"
          src={preview}
          alt="Passport preview"
          sx={{
            maxWidth: "100%",
            maxHeight: 140,
            borderRadius: 1,
            mb: 1,
            objectFit: "contain",
          }}
        />
      ) : (
        <label htmlFor="passport-scanner-file" style={{ cursor: "pointer" }}>
          <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
            <DocumentScannerIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="body2" color="primary.main">
              Scan passport to auto-fill
            </Typography>
          </Stack>
        </label>
      )}

      {preview && (
        <Button
          component="label"
          htmlFor="passport-scanner-file"
          size="small"
          sx={{ mb: 1 }}
        >
          Choose a different image
        </Button>
      )}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={onScan}
        disabled={!file || loading}
        startIcon={
          loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <DocumentScannerIcon />
          )
        }
      >
        {loading ? "Scanning…" : "Scan"}
      </Button>

      {confidence !== null && (
        <Chip
          size="small"
          color={confidenceColor(confidence)}
          label={`Confidence ${confidence}%`}
          sx={{ mt: 1 }}
        />
      )}

      {filled && (
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 1, color: "success.main", fontWeight: 600 }}
        >
          ✓ Fields filled — please review
        </Typography>
      )}

      {error && (
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 1, color: "error.main" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default PassportScanner;
